import React from 'react';
import { Table } from 'semantic-ui-react';
import { firstErrorMessage } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import {
  columnNameLink,
  columnDescription,
  columnTools,
  columnCreatedOn,
  columnCreatedBy,
  columnCreatedByLink,
} from '@/constants/fields';
import { fitToPage } from '@/hooks/paging';
import TableHeader from '@/components/table/TableHeader';
import TableWrapper from '@/components/table/TableWrapper';
import NoRowsWithPagination from '@/components/table/NoRowsWithPagination';
import NotificationsFilters from './NotificationsFilters';
import { defaultEvent } from '../subscribers/helper';
import NotificationsTableColumns from './NotificationsTableColumns';
import NotificationsPanelHeader from './NotificationsPanelHeader';
import SubscriptionEventLabel from '../subscribers/components/SubscriptionEventLabel';
import NotificationsTableRows from './NotificationsTableRows';
import Selectors from '../hooks/selectors';
import Events from '../hooks/events';

const buildColumns = ({ allowUserLink }) => {
  const columns = [
    columnNameLink, columnDescription, columnCreatedOn, columnTools,
  ];
  columns.push(allowUserLink ? columnCreatedByLink : columnCreatedBy);

  return columns;
};

const NotificationsTable = ({ category, code }) => {
  const event = defaultEvent(category);
  const state = Selectors.useNotificationsTable({ category, code, event });
  const fetchData = Events.useFetchNotifications(category, { code });
  const { items, pageNumber } = fitToPage(state.notifications, state.currentPage, state.pageSize);
  const ref = React.useRef(null);
  const columns = buildColumns(state);

  return (
    <div className="ReactTable" ref={ref}>
      <TableWrapper sticky celled className="aridhia-notifications-table">
        <Table.Header fullWidth>
          <TableHeader
            error={firstErrorMessage(state.readErrors)}
            isLoading={state.readStatus === states.PENDING && !state.name}
            numberOfColumns={columns.length}
          >
            {code && (
            <NotificationsPanelHeader
              category={category}
              code={code}
              isLoading={state.readStatus === states.PENDING}
              value={state.name}
            />
            )}
            <div className="aridhia-table-filter">
              {event && (<SubscriptionEventLabel event={event} className="dn" />)}
              <NotificationsFilters category={category} code={code} event={event} />
            </div>
          </TableHeader>
          <NotificationsTableColumns category={category} items={items} columns={columns} />
        </Table.Header>
        <Table.Body>
          <NotificationsTableRows category={category} code={code} columns={columns} event={event} items={items} />
        </Table.Body>
      </TableWrapper>
      <NoRowsWithPagination
        currentPage={pageNumber}
        entity="notification"
        goToPage={({ page }) => {
          fetchData({ event, page });
          ref.current.scrollIntoView();
        }}
        icon="bell"
        items={items}
        pageCount={state.pageCount}
        readErrors={state.readErrors}
        readStatus={state.readStatus}
        total={state.total}
      />
    </div>
  );
};

export default NotificationsTable;
